/**
 * Quy tắc cho ô nhập số (cỡ chữ, độ dày viền, độ bo góc...).
 *
 * Tách ra file riêng để test được bằng vitest ở môi trường node — không cần dựng DOM.
 */

/** Trạng thái của một lần kiểm tra số trong ô nhập. */
export type NumberInputStatus = 'valid' | 'empty' | 'not-a-number' | 'out-of-range';

export interface NumberInputCheck {
  status: NumberInputStatus;
  /** Số đọc được khi status = 'valid', null nếu không dùng được. */
  value: number | null;
}

/**
 * Đọc số trong ô nhập rồi so với khoảng [min, max].
 *
 * Số ngoài khoảng KHÔNG bị chặn về biên: nó được đánh dấu để người dùng tự sửa,
 * còn giá trị đang áp dụng giữ nguyên.
 */
export const checkNumberInput = (raw: string, min: number, max: number): NumberInputCheck => {
  const trimmed = raw.trim();
  if (trimmed === '') return { status: 'empty', value: null };

  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) return { status: 'not-a-number', value: null };
  if (parsed < min || parsed > max) return { status: 'out-of-range', value: null };

  return { status: 'valid', value: parsed };
};

/** Câu báo lỗi cho ô nhập; null khi số hợp lệ. */
export const numberInputErrorMessage = (
  status: NumberInputStatus,
  min: number,
  max: number
): string | null => {
  switch (status) {
    case 'valid':
      return null;
    case 'out-of-range':
      return `Giá trị phải từ ${min} đến ${max}`;
    default:
      return `Vui lòng nhập số từ ${min} đến ${max}`;
  }
};

/** Trạng thái của ô nhập: chuỗi đang gõ + câu báo lỗi. */
export interface NumberFieldState {
  /** Chuỗi người dùng đang gõ — tách khỏi số đang áp dụng. */
  draft: string;
  /** Câu báo lỗi; null khi số hợp lệ. */
  error: string | null;
}

/** Kết quả một lần gõ: trạng thái mới + số cần áp dụng (null = không áp dụng gì). */
export interface NumberFieldInputResult {
  state: NumberFieldState;
  applied: number | null;
}

export const createNumberFieldState = (value: number): NumberFieldState => ({
  draft: String(value),
  error: null,
});

/**
 * Người dùng gõ vào ô nhập.
 *
 * Ô nhập giữ riêng chuỗi đang gõ, tách khỏi số đang áp dụng. Nếu gắn thẳng, mọi phím
 * gõ tạm thời đều nằm ngoài [min, max] nên bị từ chối và con trỏ nhảy về số cũ —
 * người dùng chỉ còn cách bấm mũi tên tăng/giảm 1 đơn vị, không gõ được số bất kỳ.
 *
 * - Số hợp lệ → áp dụng ngay, preview đổi theo từng phím.
 * - Số ngoài khoảng → giữ nguyên số đang áp dụng, chỉ báo lỗi dưới ô nhập.
 */
export const numberFieldInput = (raw: string, min: number, max: number): NumberFieldInputResult => {
  const check = checkNumberInput(raw, min, max);
  if (check.status === 'valid') {
    return { state: { draft: raw, error: null }, applied: check.value };
  }
  return {
    state: { draft: raw, error: numberInputErrorMessage(check.status, min, max) },
    applied: null,
  };
};

/**
 * Rời ô nhập.
 * Số hợp lệ thì chuẩn hoá hiển thị (bỏ số 0 thừa, khoảng trắng thừa); số chưa hợp lệ
 * thì giữ nguyên lỗi để người dùng thấy mình chưa gõ được số mong muốn.
 */
export const numberFieldBlur = (
  state: NumberFieldState,
  min: number,
  max: number
): NumberFieldState => {
  const check = checkNumberInput(state.draft, min, max);
  if (check.status !== 'valid') return state;
  return { draft: String(check.value), error: null };
};

/**
 * Giá trị đổi từ bên ngoài (xoay preset, reset, chọn mẫu...) → ô nhập theo kịp.
 * Nếu ô đang hiện đúng số mới thì giữ nguyên chuỗi đang gõ, kẻo nhảy về dạng "18".
 */
export const numberFieldSync = (state: NumberFieldState, value: number): NumberFieldState => {
  const sameValue = state.draft.trim() !== '' && Number(state.draft) === value;
  return { draft: sameValue ? state.draft : String(value), error: null };
};
